import { useState, useCallback, useEffect } from 'react'

import { useAppDispatch } from 'redux/Hook'
import { getAllKeyPhrases, updatePhrases } from 'redux/thunks/Themes'
import { useAlert } from 'components/Shared/Alert'
import { Theme } from '../ThemeKeyPhrasesModal/Types'

interface LoadingState {
  isLoading: boolean
  message: string
}

export const useAddKeyPhrases = () => {
  const dispatch = useAppDispatch()
  const { showAlert } = useAlert()
  const [loading, setLoading] = useState<LoadingState>({
    isLoading: false,
    message: '',
  })
  const [allKeyPhrases, setAllKeyPhrases] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isAddMode, setIsAddMode] = useState(false)

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)

    return () => {
      clearTimeout(timerId)
    }
  }, [searchTerm])

  // Fetch key phrases whenever the debounced search term changes
  useEffect(() => {
    if (isAddMode) {
      fetchKeyPhrases({ search: debouncedSearchTerm })
    }
  }, [isAddMode, debouncedSearchTerm])

  const setLoadingState = useCallback((isLoading: boolean, message = '') => {
    setLoading({ isLoading, message })
  }, [])

  // Fetch all key phrases
  const fetchKeyPhrases = useCallback(
    async (search = ''): Promise<string[]> => {
      try {
        setLoadingState(true, 'Loading key phrases...')
        const result = await dispatch(getAllKeyPhrases(search))

        const keyPhrases = result.payload?.data?.attributes?.key_phrases || []
        setAllKeyPhrases(keyPhrases)
        return keyPhrases
      } catch {
        return []
      } finally {
        setLoadingState(false)
      }
    },
    [dispatch, setLoadingState],
  )

  // Execute add key phrase API call
  const addKeyPhraseToTheme = useCallback(
    async (theme: Theme, keyPhrase: string) => {
      if (!theme || !theme.id || !keyPhrase) return false

      const currentKeyPhrases =
        theme.attributes?.query?.attributes?.key_phrases || []
      const updatedKeyPhrases = [...currentKeyPhrases, keyPhrase]

      try {
        setLoadingState(true, 'Adding key phrase...')

        await dispatch(
          updatePhrases({
            themeId: theme.id,
            keyPhrases: updatedKeyPhrases,
          }),
        ).unwrap()

        showAlert({
          type: 'success',
          title: `Added key phrase "${keyPhrase}" to ${theme.attributes.name}`,
        })

        return true
      } catch {
        return false
      } finally {
        setLoadingState(false)
      }
    },
    [dispatch, setLoadingState, showAlert],
  )

  // Function to enter add mode and initialize data
  const enterAddMode = useCallback(() => {
    setAllKeyPhrases([])
    setIsAddMode(true)
  }, [])

  // Function to exit add mode
  const exitAddMode = useCallback(() => {
    setIsAddMode(false)
    setSearchTerm('')
  }, [])

  return {
    loading,
    allKeyPhrases,
    addKeyPhraseToTheme,
    searchTerm,
    setSearchTerm,
    enterAddMode,
    exitAddMode,
  }
}
